import { pxToRem } from '@fluentsms/agentnet-web-components';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { ChevronRight, ExpandMore } from '@material-ui/icons';
import { memo, useCallback, useRef, useState } from 'react';

export const useJsonTreeStyles = makeStyles((theme: Theme) => ({
  nodeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  toggleButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    width: 18,
    height: 18,
    flexShrink: 0,
    marginTop: 2,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  toggleIcon: {
    fontSize: pxToRem(16),
  },
  togglePlaceholder: {
    display: 'inline-block',
    width: 18,
    flexShrink: 0,
  },
  nodeKey: {
    color: theme.palette.primary.dark,
    fontWeight: 500,
    marginRight: 4,
    whiteSpace: 'nowrap',
  },
  nodeString: {
    color: theme.palette.success.dark,
    wordBreak: 'break-word',
  },
  nodeNumber: {
    color: theme.palette.info.dark,
  },
  nodeBoolean: {
    color: theme.palette.info.dark,
  },
  nodeNull: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  nodeBracket: {
    color: theme.palette.text.secondary,
  },
  nodePreview: {
    color: theme.palette.text.disabled,
    marginLeft: 4,
    fontSize: pxToRem(11),
  },
}));

export interface JsonNodeProps {
  nodeKey?: string | number;
  value: unknown;
  depth: number;
  defaultExpanded: boolean;
  expandVersion: number;
  isLast?: boolean;
}

function getPreview(value: unknown): string {
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    return `{${keys.length}}`;
  }
  return '';
}

const JsonNode = memo(function JsonNode(props: JsonNodeProps) {
  const { nodeKey, value, depth, defaultExpanded, expandVersion, isLast = true } = props;
  const classes = useJsonTreeStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const lastVersionRef = useRef(expandVersion);

  if (lastVersionRef.current !== expandVersion) {
    lastVersionRef.current = expandVersion;
    const shouldExpand = expandVersion > 0;
    if (expanded !== shouldExpand) {
      setExpanded(shouldExpand);
    }
  }

  const isExpandable = value !== null && typeof value === 'object';
  const indent = depth * 16;
  const comma = isLast ? '' : ',';

  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  if (!isExpandable) {
    let rendered: React.ReactNode;
    if (typeof value === 'string') {
      rendered = <span className={classes.nodeString}>&quot;{value}&quot;</span>;
    } else if (typeof value === 'number') {
      rendered = <span className={classes.nodeNumber}>{String(value)}</span>;
    } else if (typeof value === 'boolean') {
      rendered = <span className={classes.nodeBoolean}>{String(value)}</span>;
    } else {
      rendered = <span className={classes.nodeNull}>null</span>;
    }

    return (
      <div className={classes.nodeRow} style={{ paddingLeft: indent }}>
        <span className={classes.togglePlaceholder} />
        {nodeKey != null && (
          <span className={classes.nodeKey}>{typeof nodeKey === 'string' ? `"${nodeKey}"` : nodeKey}: </span>
        )}
        {rendered}
        {comma}
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [i, v] as const)
    : Object.entries(value as Record<string, unknown>);
  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';

  if (!expanded) {
    return (
      <div className={classes.nodeRow} style={{ paddingLeft: indent }}>
        <button className={classes.toggleButton} onClick={toggle} type="button" aria-label="Expand">
          <ChevronRight className={classes.toggleIcon} />
        </button>
        {nodeKey != null && (
          <span className={classes.nodeKey}>{typeof nodeKey === 'string' ? `"${nodeKey}"` : nodeKey}: </span>
        )}
        <span className={classes.nodeBracket}>{openBracket}</span>
        <span className={classes.nodePreview}>{getPreview(value)}</span>
        <span className={classes.nodeBracket}>{closeBracket}</span>
        {comma}
      </div>
    );
  }

  return (
    <div>
      <div className={classes.nodeRow} style={{ paddingLeft: indent }}>
        <button className={classes.toggleButton} onClick={toggle} type="button" aria-label="Collapse">
          <ExpandMore className={classes.toggleIcon} />
        </button>
        {nodeKey != null && (
          <span className={classes.nodeKey}>{typeof nodeKey === 'string' ? `"${nodeKey}"` : nodeKey}: </span>
        )}
        <span className={classes.nodeBracket}>{openBracket}</span>
      </div>
      {entries.map(([k, v], idx) => (
        <JsonNode
          key={String(k)}
          nodeKey={k}
          value={v}
          depth={depth + 1}
          defaultExpanded={expandVersion > 0 || (expandVersion === 0 && depth < 1)}
          expandVersion={expandVersion}
          isLast={idx === entries.length - 1}
        />
      ))}
      <div className={classes.nodeRow} style={{ paddingLeft: indent }}>
        <span className={classes.togglePlaceholder} />
        <span className={classes.nodeBracket}>{closeBracket}</span>
        {comma}
      </div>
    </div>
  );
});

export default JsonNode;
